/**
 * Activity Logger Client for trade-fw
 *
 * 문서 참고: coin-server/docs/client-integration/ACTIVITY_LOGGING_API.md
 *
 * 주요 특징:
 * - 버퍼링: 로그를 모았다가 배치 전송 (최대 50개)
 * - 주기적 전송: 30초마다 자동 flush
 * - Fire-and-forget: 로깅 실패가 앱 동작에 영향 없음
 *
 * @example
 * // 로그인 성공 시
 * activityLogger.log(Actions.Auth.LOGIN, {
 *   extraData: { method: 'email' }
 * });
 *
 * // 페이지 뷰
 * activityLogger.log(Actions.UI.PAGE_VIEW, { pageUrl: '/trade/BTCUSDT' });
 */

// Activity Log 서버 URL
const ACTIVITY_LOG_URL =
  import.meta.env.VITE_APP_ACTIVITY_LOG_URL || 'http://localhost:4400';

// ============================================================================
// 액션 타입 정의 (서버와 동일 - coin-server/packages/activity-logger/src/action-types.ts)
// ============================================================================

export const ActionCategory = {
  AUTH: 'auth',
  TRADING: 'trading',
  WALLET: 'wallet',
  SETTINGS: 'settings',
  UI: 'ui',
  SYSTEM: 'system',
  COPY_TRADE: 'copy_trade',
  BOT: 'bot',
  MISSION: 'mission',
  COMPETITION: 'competition',
} as const;

export const AuthAction = {
  LOGIN: 'auth.login',
  LOGIN_FAILED: 'auth.login_failed',
  LOGOUT: 'auth.logout',
  SIGNUP: 'auth.signup',
  SIGNUP_COMPLETE: 'auth.signup_complete',
  PASSWORD_CHANGE: 'auth.password_change',
  PASSWORD_RESET_REQUEST: 'auth.password_reset_request',
  TWOFA_ENABLE: 'auth.twofa_enable',
  TWOFA_DISABLE: 'auth.twofa_disable',
  TWOFA_VERIFY: 'auth.twofa_verify',
  SESSION_START: 'auth.session_start',
  SESSION_END: 'auth.session_end',
  TOKEN_REFRESH: 'auth.token_refresh',
  KYC_SUBMIT: 'auth.kyc_submit',
  KYC_COMPLETE: 'auth.kyc_complete',
} as const;

export const TradingAction = {
  ORDER_CREATE: 'trading.order_create',
  ORDER_CANCEL: 'trading.order_cancel',
  ORDER_MODIFY: 'trading.order_modify',
  ORDER_FILLED: 'trading.order_filled',
  ORDER_PARTIAL_FILLED: 'trading.order_partial_filled',
  POSITION_OPEN: 'trading.position_open',
  POSITION_CLOSE: 'trading.position_close',
  POSITION_PARTIAL_CLOSE: 'trading.position_partial_close',
  POSITION_MODIFY: 'trading.position_modify',
  POSITION_LIQUIDATED: 'trading.position_liquidated',
  LEVERAGE_CHANGE: 'trading.leverage_change',
  MARGIN_ADD: 'trading.margin_add',
  MARGIN_REDUCE: 'trading.margin_reduce',
  MARGIN_MODE_CHANGE: 'trading.margin_mode_change',
  TPSL_SET: 'trading.tpsl_set',
  TPSL_MODIFY: 'trading.tpsl_modify',
  TPSL_CANCEL: 'trading.tpsl_cancel',
  TP_TRIGGERED: 'trading.tp_triggered',
  SL_TRIGGERED: 'trading.sl_triggered',
  SYMBOL_CHANGE: 'trading.symbol_change',
  FAVORITE_ADD: 'trading.favorite_add',
  FAVORITE_REMOVE: 'trading.favorite_remove',
  CHART_TIMEFRAME_CHANGE: 'trading.chart_timeframe_change',
} as const;

export const WalletAction = {
  DEPOSIT_REQUEST: 'wallet.deposit_request',
  DEPOSIT_COMPLETE: 'wallet.deposit_complete',
  DEPOSIT_ADDRESS_CREATE: 'wallet.deposit_address_create',
  WITHDRAW_REQUEST: 'wallet.withdraw_request',
  WITHDRAW_APPROVE: 'wallet.withdraw_approve',
  WITHDRAW_COMPLETE: 'wallet.withdraw_complete',
  WITHDRAW_CANCEL: 'wallet.withdraw_cancel',
  WITHDRAW_ADDRESS_ADD: 'wallet.withdraw_address_add',
  WITHDRAW_ADDRESS_DELETE: 'wallet.withdraw_address_delete',
  TRANSFER: 'wallet.transfer',
  WALLET_CONVERT: 'wallet.convert',
  WALLET_VIEW: 'wallet.view',
  HISTORY_VIEW: 'wallet.history_view',
} as const;

export const SettingsAction = {
  VIEW: 'settings.view',
  CHANGE: 'settings.change',
  NOTIFICATION_CHANGE: 'settings.notification_change',
  PUSH_TOGGLE: 'settings.push_toggle',
  LANGUAGE_CHANGE: 'settings.language_change',
  THEME_CHANGE: 'settings.theme_change',
  CURRENCY_CHANGE: 'settings.currency_change',
  API_KEY_CREATE: 'settings.api_key_create',
  API_KEY_DELETE: 'settings.api_key_delete',
  PROFILE_UPDATE: 'settings.profile_update',
  AVATAR_CHANGE: 'settings.avatar_change',
} as const;

export const UIAction = {
  PAGE_VIEW: 'ui.page_view',
  PAGE_LEAVE: 'ui.page_leave',
  TAB_CHANGE: 'ui.tab_change',
  BACK: 'ui.back',
  BUTTON_CLICK: 'ui.button_click',
  LINK_CLICK: 'ui.link_click',
  CLICK: 'ui.click',
  LONG_PRESS: 'ui.long_press',
  MODAL_OPEN: 'ui.modal_open',
  MODAL_CLOSE: 'ui.modal_close',
  TOAST_SHOW: 'ui.toast_show',
  BOTTOM_SHEET_OPEN: 'ui.bottom_sheet_open',
  BOTTOM_SHEET_CLOSE: 'ui.bottom_sheet_close',
  SEARCH: 'ui.search',
  SEARCH_RESULT_CLICK: 'ui.search_result_click',
  FILTER_CHANGE: 'ui.filter_change',
  SORT_CHANGE: 'ui.sort_change',
  COPY: 'ui.copy',
  SHARE: 'ui.share',
} as const;

export const SystemAction = {
  APP_START: 'system.app_start',
  APP_CLOSE: 'system.app_close',
  APP_BACKGROUND: 'system.app_background',
  APP_FOREGROUND: 'system.app_foreground',
  ERROR: 'system.error',
  API_ERROR: 'system.api_error',
  NETWORK_ERROR: 'system.network_error',
  AUTH_ERROR: 'system.auth_error',
  PERMISSION_ERROR: 'system.permission_error',
  VALIDATION_ERROR: 'system.validation_error',
  WS_CONNECT: 'system.ws_connect',
  WS_DISCONNECT: 'system.ws_disconnect',
  NETWORK_ONLINE: 'system.network_online',
  NETWORK_OFFLINE: 'system.network_offline',
} as const;

export const CopyTradeAction = {
  START: 'copy_trade.start',
  STOP: 'copy_trade.stop',
  SETTINGS_CHANGE: 'copy_trade.settings_change',
  TRADER_FOLLOW: 'copy_trade.trader_follow',
  TRADER_UNFOLLOW: 'copy_trade.trader_unfollow',
  TRADER_VIEW: 'copy_trade.trader_view',
  POSITION_MANUAL_CLOSE: 'copy_trade.position_manual_close',
} as const;

export const BotAction = {
  CREATE: 'bot.create',
  START: 'bot.start',
  STOP: 'bot.stop',
  DELETE: 'bot.delete',
  SETTINGS_CHANGE: 'bot.settings_change',
  VIEW: 'bot.view',
  PROFIT_VIEW: 'bot.profit_view',
} as const;

export const MissionAction = {
  LIST_VIEW: 'mission.list_view',
  DETAIL_VIEW: 'mission.detail_view',
  START: 'mission.start',
  COMPLETE: 'mission.complete',
  REWARD_CLAIM: 'mission.reward_claim',
  ATTENDANCE: 'mission.attendance',
  EVENT_JOIN: 'mission.event_join',
} as const;

export const CompetitionAction = {
  LIST_VIEW: 'competition.list_view',
  DETAIL_VIEW: 'competition.detail_view',
  JOIN: 'competition.join',
  LEAVE: 'competition.leave',
  RANKING_VIEW: 'competition.ranking_view',
  REWARD_CLAIM: 'competition.reward_claim',
} as const;

export const Actions = {
  Auth: AuthAction,
  Trading: TradingAction,
  Wallet: WalletAction,
  Settings: SettingsAction,
  UI: UIAction,
  System: SystemAction,
  CopyTrade: CopyTradeAction,
  Bot: BotAction,
  Mission: MissionAction,
  Competition: CompetitionAction,
} as const;

// ============================================================================
// 타입 정의 (문서 스펙과 일치)
// ============================================================================

interface ActivityLog {
  /** 액션 타입 (필수) - Actions.Auth.LOGIN 등 사용 */
  actionType: string;
  /** 액션 카테고리 (자동 추론 가능) */
  actionCategory?: string;
  /** 상세 설명 */
  actionDetail?: string;
  /** 현재 페이지 URL/경로 */
  pageUrl?: string;
  /** 추가 데이터 (최대 10KB, 깊이 3) */
  extraData?: Record<string, unknown>;
  /** 클라이언트 타임스탬프 (로그 생성 시점) - 내부 사용 */
  clientTimestamp?: string;
}

interface ActivityLogRequest {
  /** 세션 고유 ID (앱 실행마다 새로 생성) */
  sessionId: string;
  /** 플랫폼 */
  platform: 'web' | 'ios' | 'android';
  /** 로그 배열 (최대 50개) */
  logs: ActivityLog[];
  /** 로그인한 유저 ID (선택) */
  userId?: number;
  /** 앱 버전 (선택) */
  appVersion?: string;
  /** 화면 해상도 (선택) */
  screenResolution?: string;
  /** 언어 코드 (선택) */
  language?: string;
  /** 타임존 (선택) */
  timezone?: string;
}

// ============================================================================
// 세션 및 유틸리티
// ============================================================================

const SESSION_KEY = 'activity_session_id';
const MAX_BUFFER_SIZE = 50;
const FLUSH_INTERVAL_MS = 30000; // 30초

/**
 * UUID v4 생성
 * - 브라우저: crypto.randomUUID() 사용
 * - 폴백: Math.random() 기반 생성
 */
function generateUUID(): string {
  // 최신 브라우저는 crypto.randomUUID() 지원
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // 폴백: RFC 4122 v4 UUID 형식
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getOrCreateSessionId(): string {
  let sessionId = sessionStorage.getItem(SESSION_KEY);
  if (!sessionId) {
    sessionId = generateUUID();
    sessionStorage.setItem(SESSION_KEY, sessionId);
  }
  return sessionId;
}

function getAccessToken(): string | undefined {
  try {
    const storageData = localStorage.getItem('token-storage');
    if (storageData) {
      const { state } = JSON.parse(storageData);
      const token = state?.accessToken;
      if (import.meta.env.DEV && token) {
        console.log('[ActivityLogger] Token found, length:', token.length);
      }
      return token;
    }
    if (import.meta.env.DEV) {
      console.log('[ActivityLogger] No token-storage found in localStorage');
    }
  } catch (e) {
    if (import.meta.env.DEV) {
      console.warn('[ActivityLogger] Failed to parse token-storage:', e);
    }
  }
  return undefined;
}

function getScreenResolution(): string {
  return `${window.screen.width}x${window.screen.height}`;
}

// ============================================================================
// Activity Logger 클래스 (버퍼링 + 배치 전송)
// ============================================================================

class ActivityLogger {
  private baseUrl: string;
  private enabled: boolean;
  private buffer: ActivityLog[] = [];
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private isFlushing: boolean = false; // 중복 flush 방지 플래그

  constructor() {
    this.baseUrl = ACTIVITY_LOG_URL;
    this.enabled =
      import.meta.env.DEV || import.meta.env.VITE_APP_ACTIVITY_LOG_ENABLED === 'true';

    // 주기적 flush (30초마다)
    if (this.enabled && typeof window !== 'undefined') {
      this.flushTimer = setInterval(() => this.flush(), FLUSH_INTERVAL_MS);

      // 페이지 이탈 시 flush (beforeunload와 visibilitychange 중 하나만 실행되도록)
      // pagehide는 beforeunload보다 더 신뢰성 있음 (모바일 포함)
      window.addEventListener('pagehide', () => this.flush());

      // visibilitychange는 탭 전환 시에만 (페이지 이탈은 pagehide가 처리)
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.flush();
        }
      });
    }
  }

  /**
   * 로그 추가 (버퍼에 저장)
   *
   * @example
   * activityLogger.log(Actions.Auth.LOGIN, { extraData: { method: 'email' } });
   * activityLogger.log(Actions.UI.PAGE_VIEW, { pageUrl: '/trade/BTCUSDT' });
   */
  log(
    actionType: string,
    options?: {
      actionDetail?: string;
      pageUrl?: string;
      extraData?: Record<string, unknown>;
    }
  ): void {
    if (!this.enabled) return;

    const logEntry: ActivityLog = {
      actionType,
      ...(options?.actionDetail && { actionDetail: options.actionDetail }),
      ...(options?.pageUrl && { pageUrl: options.pageUrl }),
      ...(options?.extraData && { extraData: options.extraData }),
      // 로그 생성 시점의 클라이언트 타임스탬프 (배치 전송 시 지연 보정용)
      clientTimestamp: new Date().toISOString(),
    };

    // 기본 pageUrl 설정
    if (!logEntry.pageUrl && typeof window !== 'undefined') {
      logEntry.pageUrl = window.location.pathname;
    }

    this.buffer.push(logEntry);

    // 버퍼가 가득 차면 즉시 전송
    if (this.buffer.length >= MAX_BUFFER_SIZE) {
      this.flush();
    }
  }

  /**
   * 버퍼 전송 (배치)
   * @param overrideUserId - userId를 직접 지정 (로그인 시 토큰 저장 전에 사용)
   * @param overrideToken - 토큰을 직접 지정 (로그인 시 토큰 저장 전에 사용)
   */
  async flush(overrideUserId?: number, overrideToken?: string): Promise<boolean> {
    // 버퍼가 비어있으면 스킵
    if (!this.enabled || this.buffer.length === 0) {
      return false;
    }

    // 중복 flush 방지 (동시에 여러 이벤트가 발생할 때)
    if (this.isFlushing) {
      if (import.meta.env.DEV) {
        console.log('[ActivityLogger] flush() skipped - already flushing');
      }
      return false;
    }

    this.isFlushing = true;
    const logs = [...this.buffer];
    this.buffer = [];

    const request: ActivityLogRequest = {
      sessionId: getOrCreateSessionId(),
      platform: 'web',
      logs,
      userId: overrideUserId, // 백엔드에서 JWT로 자동 추출하므로 선택적
      screenResolution: getScreenResolution(),
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    // Authorization 헤더 구성 (백엔드에서 JWT 파싱하여 userId 추출)
    const token = overrideToken ?? getAccessToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    if (import.meta.env.DEV) {
      console.log('[ActivityLogger] flush() called:', {
        logsCount: logs.length,
        hasToken: !!token,
        tokenLength: token?.length,
        overrideUserId: overrideUserId,
        actionTypes: logs.map(l => l.actionType),
      });
    }

    try {
      const response = await fetch(`${this.baseUrl}/api/logs/activity`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        if (import.meta.env.DEV) {
          console.warn('[ActivityLogger] Failed to send logs:', response.status);
        }
        return false;
      }

      const result = await response.json();
      if (import.meta.env.DEV) {
        console.log('[ActivityLogger] Logs sent:', { count: logs.length, result });
      }
      return result.success;
    } catch (error) {
      // Fire-and-forget: 실패해도 앱 동작에 영향 없음
      if (import.meta.env.DEV) {
        console.warn('[ActivityLogger] Error sending logs:', error);
      }
      return false;
    } finally {
      this.isFlushing = false;
    }
  }

  /**
   * 로그인 성공 로깅
   * @param method - 로그인 방식: 'email' | 'phone' | 'google' | 'apple'
   * @param userId - 로그인한 유저 ID (토큰 저장 전에 호출되므로 직접 전달 필요)
   * @param accessToken - 로그인 시 받은 토큰 (백엔드에서 JWT 파싱용)
   */
  logLogin(
    method: 'email' | 'phone' | 'google' | 'apple',
    userId?: number,
    accessToken?: string
  ): void {
    this.log(Actions.Auth.LOGIN, {
      pageUrl: '/login',
      extraData: { method },
    });
    // 로그인은 중요 이벤트이므로 즉시 전송
    // userId, accessToken이 전달되면 직접 사용 (토큰 저장 전이므로)
    this.flush(userId, accessToken);
  }

  /**
   * 로그인 실패 로깅
   */
  logLoginFailed(method: 'email' | 'phone' | 'google' | 'apple', reason: string): void {
    this.log(Actions.Auth.LOGIN_FAILED, {
      pageUrl: '/login',
      extraData: { method, reason },
    });
    // 실패도 중요 이벤트이므로 즉시 전송
    this.flush();
  }

  /**
   * 로그아웃 로깅
   *
   * ⚠️ IMPORTANT: 토큰 삭제(removeTokens) 전에 호출해야 함!
   * 토큰이 삭제된 후에는 userId를 추출할 수 없어 NULL로 기록됨
   *
   * @param accessToken - 현재 액세스 토큰 (토큰 삭제 전 전달 필수)
   */
  logLogout(accessToken?: string): void {
    // 로그아웃 로그를 버퍼에 추가
    this.log(Actions.Auth.LOGOUT);

    // 버퍼에 쌓인 모든 로그를 현재 토큰으로 한 번에 flush
    // (토큰 삭제 전에 호출되어야 userId가 포함됨)
    this.flush(undefined, accessToken);
  }

  /**
   * 페이지 뷰 로깅
   */
  logPageView(pageName: string, pageUrl?: string): void {
    this.log(Actions.UI.PAGE_VIEW, {
      actionDetail: pageName,
      pageUrl: pageUrl || window.location.pathname,
    });
  }

  /**
   * 에러 로깅
   */
  logError(
    errorType: 'error' | 'api_error' | 'network_error' | 'auth_error',
    extraData: Record<string, unknown>
  ): void {
    const actionMap = {
      error: Actions.System.ERROR,
      api_error: Actions.System.API_ERROR,
      network_error: Actions.System.NETWORK_ERROR,
      auth_error: Actions.System.AUTH_ERROR,
    };
    this.log(actionMap[errorType], { extraData });
  }

  /**
   * 정리 (컴포넌트 언마운트 시)
   */
  destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    this.flush();
  }
}

// 싱글톤 인스턴스 export
export const activityLogger = new ActivityLogger();
export default activityLogger;
